import * as React from 'react'
import * as Papa from 'papaparse'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { API, ICustomer, PermissionNames } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import moment from 'moment'
import { message } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { Alert } from 'antd-v4'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  exportInProgress: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
export class CustomerExportButton extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!
  CUSTOMER_LIMIT = 3000

  constructor(props: Props) {
    super(props)

    this.state = {
      exportInProgress: false,
    }
  }

  export = async () => {
    this.setState({ exportInProgress: true })

    try {
      const customerJSON = await this.createCustomerJSON()
      const csvString = Papa.unparse(customerJSON)

      // Reference: https://github.com/mholt/PapaParse/issues/175
      const blob = new Blob([csvString])

      if (navigator?.msSaveBlob) {
        navigator.msSaveBlob(blob, `customers-${moment().format('YYYY-MM-DD')}.csv`)
      } else {
        const a = window.document.createElement('a')
        a.href = (window.URL as any).createObjectURL(blob, { type: 'text/plain' } as any)
        a.download = 'customers.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while exporting the customer list')
    } finally {
      this.setState({ exportInProgress: false })
    }
  }

  createCustomerJSON = async () => {
    const paginatedCustomers = await API.getCustomers(this.company.id, this.CUSTOMER_LIMIT, 0)

    let customers = [...paginatedCustomers.customers]

    if (paginatedCustomers.total > this.CUSTOMER_LIMIT) {
      for (
        let offset = this.CUSTOMER_LIMIT;
        offset < paginatedCustomers.total;
        offset += this.CUSTOMER_LIMIT
      ) {
        const additionalCustomers = await API.getCustomers(
          this.company.id,
          this.CUSTOMER_LIMIT,
          offset,
        )
        customers = [...customers, ...additionalCustomers.customers]
      }
    }

    return this.convertCustomerForCSV(customers)
  }

  convertCustomerForCSV(customers: ICustomer[]) {
    return customers.map((customer) => ({
      Name: customer.name,
      'Greenline ID': customer.id,
      'Card ID': customer.cardId,
      Phone: customer.phone,
      Email: customer.email,
      'Company ID': customer.companyId,
      'Sign Up Location': customer.locationName,
      Address: customer.address,
      City: customer.city,
      Country: customer.country,
      'Postal Code': customer.postalCode,
      Birthday: customer.birthday ? moment(customer.birthday).format('YYYY-MM-DD') : null,
      Gender: customer.gender,
      'Is Tax Exempt': customer.isTaxExempt,
      'Loyalty Tier': customer.loyaltyTierName,
      'Create Date': moment(customer.createDate).format('YYYY-MM-DD hh:mma'),
      'Total Spend': this.canViewCustomerTotalSpend ? (customer.totalSpend || 0) / 100 : undefined,
      Notes: customer.notes,
      'Loyalty Balance': customer.creditBalance,
    }))
  }

  get canViewCustomerTotalSpend() {
    return !!this.props.currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      this.company.id,
    )
  }

  render() {
    const { exportInProgress } = this.state

    return (
      <>
        {!this.canViewCustomerTotalSpend && (
          <Alert
            className='mt-3 mb-3'
            type='warning'
            showIcon
            message={
              <div>
                <div>{`You do not have "${PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND}" permission.`}</div>
                <div>Total Spend field will be removed.</div>
              </div>
            }
          />
        )}

        <BlazeButton
          buttonProps={{
            icon: 'download',
            onClick: this.export,
            loading: exportInProgress,
            disabled: exportInProgress,
          }}
          titleProps={{
            title: 'Export csv',
          }}
        />
      </>
    )
  }
}
